import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stethoscope, Eye, EyeOff, LogIn } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { CLINIC_NAME } from '../utils/clinicConfig';
import Logo3D from '../components/Logo3D';

export default function LoginPage() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const { login } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (!username.trim() || !password) {
      setError('Please enter both username and password.');
      return;
    }
    setLoading(true);
    try {
      const userData = await login(username.trim(), password);
      showToast(`Welcome back, ${userData.displayName || userData.username}`, 'success');
      navigate(userData.role === 'doctor' ? '/doctor' : '/receptionist');
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-bg)',
        padding: 20,
      }}
    >
      <div style={{ width: '100%', maxWidth: 460 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
            <Logo3D icon={<Stethoscope size={44} color="white" />} size={90} radius={20} />
          </div>
          <h1 style={{ fontSize: 28, fontWeight: 800, margin: '0 0 6px', letterSpacing: '-0.02em' }}>
            {CLINIC_NAME}
          </h1>
          <p style={{ fontSize: 16, color: 'var(--color-ink-soft)', margin: 0 }}>
            Queue Management System
          </p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 16 }}>
            <img src="/agsoft-labs-logo.png" alt="AGSOFT LAB" style={{ width: 80, height: 80, borderRadius: 14, flexShrink: 0, objectFit: 'cover' }} />
            <span style={{ fontSize: 11, color: 'var(--color-ink-faint)' }}>
              Developed by <strong>AGSOFT LAB</strong>
            </span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="card" style={{ padding: 32 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: '0 0 20px' }}>Sign in to continue</h2>

          {error && (
            <div
              style={{
                background: 'var(--color-danger-soft)',
                color: 'var(--color-danger)',
                padding: '10px 14px',
                borderRadius: 8,
                fontSize: 13,
                marginBottom: 16,
              }}
            >
              {error}
            </div>
          )}

          <div className="field">
            <label className="label" htmlFor="username">Username</label>
            <input
              id="username"
              className="input"
              type="text"
              autoComplete="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="doctor or reception"
              autoFocus
            />
          </div>

          <div className="field">
            <label className="label" htmlFor="password">Password</label>
            <div style={{ position: 'relative' }}>
              <input
                id="password"
                className="input"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                style={{ paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                style={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-ink-faint)',
                  display: 'flex',
                }}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <button type="submit" className="btn btn-primary" style={{ width: '100%', marginTop: 8 }} disabled={loading}>
            <LogIn size={16} />
            {loading ? 'Signing in...' : 'Sign in'}
          </button>
        </form>

        <p style={{ textAlign: 'center', fontSize: 12, color: 'var(--color-ink-faint)', marginTop: 18 }}>
          Both doctor and receptionist accounts sign in here. You'll be taken to your portal automatically.
        </p>
      </div>
    </div>
  );
}
